import L from 'leaflet';
import * as turf from '@turf/turf';
import type { GridParams } from 'contexts/GridContext';
import { PROJECT_LOCATION_RADIUS_METERS } from 'views/add-project/MandatoryFields';
import {
  analysisAreaIntersectsProjectRadius,
  buildProjectLocationRadiusPolygon,
  buildTurfPolygonFromLatLngs,
  buildTurfPolygonFromLv95,
  isPolygonContained,
  requiresProjectRadiusValidation,
} from './cadastralGeometry';
import type { BorderType, Lv95Coordinate, TurfPolygon } from './cadastralShared';

interface CadastralValidatorOptions {
  projectCoordinates?: { lat: number; lng: number } | null;
  analysisAreas: Lv95Coordinate[][];
  gridParams: GridParams;
  legalBorderRef: { current: L.Polygon | null };
  buildableBorderRef: { current: L.Polygon | null };
  notifyError: (message: string) => void;
}

const createCadastralValidators = ({
  projectCoordinates,
  analysisAreas,
  gridParams,
  legalBorderRef,
  buildableBorderRef,
  notifyError,
}: CadastralValidatorOptions) => {
  const validateProjectRadiusRequirement = (
    polygonType: string | null,
    polygon: TurfPolygon | null,
    analysisAreaTypeOverride = gridParams.analysisAreaType
  ) => {
    if (!requiresProjectRadiusValidation(polygonType, analysisAreaTypeOverride)) return true;
    const projectRadius = buildProjectLocationRadiusPolygon(projectCoordinates, PROJECT_LOCATION_RADIUS_METERS / 1000);
    if (analysisAreaIntersectsProjectRadius(polygon, projectRadius)) return true;
    notifyError('The analysis area must include at least a portion within 100 m of the project location.');
    return false;
  };

  const validateAnalysisAreaSeparation = (
    polygonType: BorderType | null,
    polygon: TurfPolygon | null,
    options: { ignoreIndex?: number } = {}
  ) => {
    if (polygonType !== 'analysisArea' || !polygon) return true;
    const intersectsOtherAnalysisArea = analysisAreas.some((coordinates, index) => {
      if (index === options.ignoreIndex || !Array.isArray(coordinates) || coordinates.length < 3) return false;
      const otherPolygon = buildTurfPolygonFromLv95(coordinates);
      if (!otherPolygon) return false;
      try {
        return turf.booleanIntersects(polygon, otherPolygon);
      } catch {
        return false;
      }
    });
    if (!intersectsOtherAnalysisArea) return true;
    notifyError('Analysis areas cannot intersect each other.');
    return false;
  };

  const validatePolygonDoesNotSelfIntersect = (_polygonType: BorderType | null, polygon: TurfPolygon | null) => {
    if (!polygon || turf.kinks(polygon).features.length === 0) return true;
    notifyError('Border intersects itself!');
    return false;
  };

  const validateLegalBuildableContainment = (polygonType: BorderType | null, polygon: TurfPolygon | null) => {
    if (!polygon) return true;
    if (polygonType === 'buildableBorder') {
      const legalPolygon =
        buildTurfPolygonFromLatLngs(legalBorderRef.current?._latlngs?.[0]) ||
        buildTurfPolygonFromLv95(gridParams.legalBorder);
      if (legalPolygon && !isPolygonContained(legalPolygon, polygon)) {
        notifyError('Buildable Area must stay within or coincide with Property Area!');
        return false;
      }
    }
    if (polygonType === 'legalBorder') {
      const buildablePolygon =
        buildTurfPolygonFromLatLngs(buildableBorderRef.current?._latlngs?.[0]) ||
        buildTurfPolygonFromLv95(gridParams.buildableBorder);
      if (buildablePolygon && !isPolygonContained(polygon, buildablePolygon)) {
        notifyError('Property Area must contain or coincide with Buildable Area!');
        return false;
      }
    }
    return true;
  };

  return {
    validateProjectRadiusRequirement,
    validateAnalysisAreaSeparation,
    validatePolygonDoesNotSelfIntersect,
    validateLegalBuildableContainment,
  };
};

export default createCadastralValidators;
